// src/pages/EditProduct.jsx
import React, { useState, useEffect, useMemo } from 'react';
import usePersistentLanguage from '../hooks/usePersistentLanguage';
import styles from './EditProduct.module.css';
import { getProducts, updateProduct, deleteProduct } from '../api/productApi';
import AppLayout from '../components/AppLayout';
import ProductImage from '../components/ProductImage';

// 1) Translation strings for English and Arabic
const translations = {
  en: {
    manageProducts: 'Manage Products',
    // Errors / Messages
    fetchProductsError: 'Failed to fetch products. Please try again.',
    onlyCertainImages: 'Only JPEG, JPG, PNG, and GIF files are allowed.',
    lessThan5MB: 'File size should be less than 5 MB.',
    nameSizeSKURequired: 'Name, size, and SKU are required.',
    allSortsValid: 'All sorts must have a valid name.',
    allColorsValid: 'All colors must have a valid name.',
    failedUpdateProduct: 'Failed to update product. Please try again.',
    failedDeleteProduct: 'Failed to delete product. Please try again.',
    // Button Labels
    edit: 'Edit',
    delete: 'Delete',
    confirmDeleteTitle: 'Confirm Deletion',
    confirmDeleteMessage: 'Are you sure you want to delete the product with SKU:',
    yesDelete: 'Yes, Delete',
    cancel: 'Cancel',
    warning: 'Warning',
    close: 'Close',
    // Edit Modal
    editProductTitle: 'Edit Product',
    currentImage: 'Current Image:',
    noCurrentImage: 'No Current Image',
    updateImageOptional: 'Update Product Image (Optional):',
    sortsHeading: 'Sorts',
    sortNamePlaceholder: 'Sort name',
    colorsQuantitiesHeading: 'Colors & Quantities',
    colorNamePlaceholder: 'Color name',
    quantityPlaceholder: 'Quantity',
    removeColor: 'Remove Color',
    addAnotherColor: 'Add Another Color',
    removeSort: 'Remove Sort',
    addAnotherSort: 'Add Another Sort',
    saveChanges: 'Save Changes',
    showDetails: 'Show Details',
    hideDetails: 'Hide Details',
    showColors: 'Show Colors',
    hideColors: 'Hide Colors',
    available: 'Available',
    held: 'Held',
    totalAvailable: 'Total available',
    totalHeld: 'Total held',
    totalWithHold: 'Total with hold',
    price: 'Price',
    availableValue: 'Available value',
    valueWithHold: 'Value with hold',
    noImage: 'No Image',
    loadingImage: 'Loading image...',
    imageUnavailable: 'Image unavailable',
    // Fields
    nameLabel: 'Name:',
    sizeLabel: 'Size:',
    skuLabel: 'SKU:',
    homepage: 'Homepage',
  },
  ar: {
    manageProducts: 'إدارة المنتجات',
    // Errors / Messages
    fetchProductsError: 'فشل في جلب المنتجات. حاول مرة أخرى.',
    onlyCertainImages: 'مسموح فقط بملفات JPEG, JPG, PNG, و GIF.',
    lessThan5MB: 'يجب أن يكون حجم الملف أقل من 5 ميجابايت.',
    nameSizeSKURequired: 'الاسم والحجم وSKU مطلوبة.',
    allSortsValid: 'يجب أن تحتوي جميع الأصناف على اسم صالح.',
    allColorsValid: 'يجب أن تحتوي جميع الألوان على اسم صالح.',
    failedUpdateProduct: 'فشل في تحديث المنتج. حاول مرة أخرى.',
    failedDeleteProduct: 'فشل في حذف المنتج. حاول مرة أخرى.',
    // Button Labels
    edit: 'تعديل',
    delete: 'حذف',
    confirmDeleteTitle: 'تأكيد الحذف',
    confirmDeleteMessage: 'هل أنت متأكد أنك تريد حذف المنتج بالرمز SKU:',
    yesDelete: 'نعم، احذف',
    cancel: 'إلغاء',
    warning: 'تحذير',
    close: 'إغلاق',
    // Edit Modal
    editProductTitle: 'تعديل المنتج',
    currentImage: 'الصورة الحالية:',
    noCurrentImage: 'لا توجد صورة حالية',
    updateImageOptional: 'تحديث صورة المنتج (اختياري):',
    sortsHeading: 'الأصناف',
    sortNamePlaceholder: 'اسم الصنف',
    colorsQuantitiesHeading: 'الألوان والكميات',
    colorNamePlaceholder: 'اسم اللون',
    quantityPlaceholder: 'الكمية',
    removeColor: 'إزالة اللون',
    addAnotherColor: 'إضافة لون آخر',
    removeSort: 'إزالة الصنف',
    addAnotherSort: 'إضافة صنف آخر',
    saveChanges: 'حفظ التعديلات',
    showDetails: 'عرض التفاصيل',
    hideDetails: 'إخفاء التفاصيل',
    showColors: 'عرض الألوان',
    hideColors: 'إخفاء الألوان',
    available: 'المتاح',
    held: 'محجوز',
    totalAvailable: 'إجمالي المتاح',
    totalHeld: 'إجمالي المحجوز',
    totalWithHold: 'الإجمالي مع المحجوز',
    price: 'السعر',
    availableValue: 'قيمة المتاح',
    valueWithHold: 'القيمة مع المحجوز',
    noImage: 'لا توجد صورة',
    loadingImage: 'جارٍ تحميل الصورة...',
    imageUnavailable: 'الصورة غير متاحة',
    // Fields
    nameLabel: 'الاسم:',
    sizeLabel: 'الحجم:',
    skuLabel: 'رمز المنتج (SKU):',
    homepage: 'الصفحة الرئيسية',
  },
};

const EditProduct = () => {
  // 2) Add language state and translation helper
  const [language, setLanguage] = usePersistentLanguage();
  const t = (key) => translations[language][key];

  // 3) Toggle language function
  const toggleLanguage = () => {
    setLanguage((prev) => (prev === 'en' ? 'ar' : 'en'));
  };

  const [products, setProducts] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [confirmDeleteSKU, setConfirmDeleteSKU] = useState(null);
  const [expandedProducts, setExpandedProducts] = useState({});
  const [expandedSorts, setExpandedSorts] = useState({});

  // **4) Add loading state**
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getProducts()
      .then((response) => {
        setProducts(response.data);
        setLoading(false); // **Set loading to false after data is fetched**
      })
      .catch((error) => {
        console.error('Error fetching products:', error);
        setErrorMessage(t('fetchProductsError'));
        setLoading(false); // **Set loading to false even if there's an error**
      });
    // eslint-disable-next-line
  }, []);

  const handleEditClick = (product) => {
    const productCopy = {
      ...product,
      sorts: product.sorts.map((sort) => ({
        ...sort,
        colors: sort.colors.map((color) => ({ ...color })),
      })),
      imageFile: null,
    };
    setEditingProduct(productCopy);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        alert(t('onlyCertainImages'));
        e.target.value = null;
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert(t('lessThan5MB'));
        e.target.value = null;
        return;
      }
      setEditingProduct({ ...editingProduct, imageFile: file });
    } else {
      setEditingProduct({ ...editingProduct, imageFile: null });
    }
  };

  const handleSortChange = (index, field, value) => {
    const updatedSorts = [...editingProduct.sorts];
    updatedSorts[index][field] = value;
    setEditingProduct({ ...editingProduct, sorts: updatedSorts });
  };

  const handleAddSort = () => {
    setEditingProduct({
      ...editingProduct,
      sorts: [
        ...editingProduct.sorts,
        { sortName: '', colors: [{ color: '', quantity: 0 }] },
      ],
    });
  };

  const handleRemoveSort = (index) => {
    const updatedSorts = [...editingProduct.sorts];
    updatedSorts.splice(index, 1);
    setEditingProduct({ ...editingProduct, sorts: updatedSorts });
  };

  const handleColorChange = (sortIndex, colorIndex, field, value) => {
    const updatedSorts = [...editingProduct.sorts];
    updatedSorts[sortIndex].colors[colorIndex][field] =
      field === 'quantity' ? parseInt(value, 10) || 0 : value;
    setEditingProduct({ ...editingProduct, sorts: updatedSorts });
  };

  const handleAddColor = (sortIndex) => {
    const updatedSorts = [...editingProduct.sorts];
    updatedSorts[sortIndex].colors.push({ color: '', quantity: 0 });
    setEditingProduct({ ...editingProduct, sorts: updatedSorts });
  };

  const handleRemoveColor = (sortIndex, colorIndex) => {
    const updatedSorts = [...editingProduct.sorts];
    updatedSorts[sortIndex].colors.splice(colorIndex, 1);
    setEditingProduct({ ...editingProduct, sorts: updatedSorts });
  };

  const validateForm = () => {
    if (
      !editingProduct.name.trim() ||
      !editingProduct.size.trim() ||
      !editingProduct.SKU.trim()
    ) {
      setErrorMessage(t('nameSizeSKURequired'));
      return false;
    }
    for (let sort of editingProduct.sorts) {
      if (!sort.sortName.trim()) {
        setErrorMessage(t('allSortsValid'));
        return false;
      }
      for (let color of sort.colors) {
        if (!color.color.trim()) {
          setErrorMessage(t('allColorsValid'));
          return false;
        }
      }
    }
    return true;
  };

  const handleSaveChanges = async () => {
    if (!validateForm()) return;
    const { _id, name, size, SKU, sorts, imageFile } = editingProduct;
    try {
      const formData = new FormData();
      formData.append('name', name);
      formData.append('size', size);
      formData.append('SKU', SKU);
      formData.append('sorts', JSON.stringify(sorts));
      if (imageFile) {
        formData.append('image', imageFile);
      }

      setLoading(true); // **Start loading before API call**

      const updated = await updateProduct(_id, formData);
      setProducts((prev) => prev.map((p) => (p._id === _id ? updated.data : p)));
      if (imageFile) {
        setExpandedProducts((prev) => ({ ...prev, [_id]: false }));
      }
      setEditingProduct(null);
      setLoading(false); // **Stop loading after successful update**
    } catch (error) {
      console.error('Error updating product:', error);
      setErrorMessage(t('failedUpdateProduct'));
      setLoading(false); // **Stop loading even if there's an error**
    }
  };

  const token = useMemo(() => localStorage.getItem('authToken'), []);
  const userInfo = useMemo(() => {
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          return {
            userType: payload.userType,
            requesterUsername: payload.username,
          };
        } catch (error) {
          console.error('Error parsing token payload:', error);
          return { userType: null, requesterUsername: null };
        }
      }
      return { userType: null, requesterUsername: null };
    }, [token]);
    
  const handleCancelEdit = () => {
    setEditingProduct(null);
  };

  const handleCloseModal = () => {
    setErrorMessage(null);
  };

  const handleDeleteRequest = (sku) => {
    setConfirmDeleteSKU(sku);
  };

  const handleDeleteConfirm = async () => {
    if (!confirmDeleteSKU) return;
    try {
      setLoading(true); // **Start loading before API call**
      await deleteProduct(confirmDeleteSKU);
      setProducts((prev) =>
        prev.filter((product) => product.SKU !== confirmDeleteSKU)
      );
      setLoading(false); // **Stop loading after successful deletion**
    } catch (error) {
      console.error('Error deleting product:', error);
      setErrorMessage(t('failedDeleteProduct'));
      setLoading(false); // **Stop loading even if there's an error**
    }
    setConfirmDeleteSKU(null);
  };

  const handleDeleteCancel = () => {
    setConfirmDeleteSKU(null);
  };

  const toggleProduct = (productId) => {
    setExpandedProducts((prev) => ({ ...prev, [productId]: !prev[productId] }));
  };

  const toggleSort = (sortKey) => {
    setExpandedSorts((prev) => ({ ...prev, [sortKey]: !prev[sortKey] }));
  };

  const getSortTotals = (sort) => {
    const colors = Array.isArray(sort.colors) ? sort.colors : [];
    const price = Number(sort.price) || 0;
    const available = colors.reduce((total, color) => total + (Number(color.quantity) || 0), 0);
    const held = colors.reduce((total, color) => total + (Number(color.reservedQuantity) || 0), 0);
    return {
      available,
      held,
      totalWithHold: available + held,
      availableValue: available * price,
      totalWithHoldValue: (available + held) * price,
    };
  };

  const getProductTotals = (product) =>
    (Array.isArray(product.sorts) ? product.sorts : []).reduce(
      (totals, sort) => {
        const sortTotals = getSortTotals(sort);
        return {
          available: totals.available + sortTotals.available,
          held: totals.held + sortTotals.held,
          totalWithHold: totals.totalWithHold + sortTotals.totalWithHold,
          availableValue: totals.availableValue + sortTotals.availableValue,
          totalWithHoldValue: totals.totalWithHoldValue + sortTotals.totalWithHoldValue,
        };
      },
      {
        available: 0,
        held: 0,
        totalWithHold: 0,
        availableValue: 0,
        totalWithHoldValue: 0,
      }
    );

  const formatPrice = (value) => {
    const numericValue = Number(value || 0);
    try {
      return new Intl.NumberFormat(language === 'ar' ? 'ar-EG' : 'en-US', {
        style: 'currency',
        currency: 'EGP',
        minimumFractionDigits: 2,
      }).format(numericValue);
    } catch (error) {
      return `${numericValue.toFixed(2)} EGP`;
    }
  };

  return (
    <AppLayout
      title={t('manageProducts')}
      language={language}
      onToggleLanguage={toggleLanguage}
      userType={userInfo.userType}
      showHome
      homeLabel={t('homepage')}
      contentClassName={styles.manageProductsPage}
      contentProps={{ 'aria-busy': loading }}
    >
      {/* **5) Loading Indicator** */}
      {loading && (
        <div className={styles.loadingOverlay}>
          <div className={styles.spinner}></div>
        </div>
      )}

      {/* **6) Main Content - Hidden when loading** */}
      {!loading && (
        <>
          <div className={styles.productGrid}>
            {products.map((product) => {
              const isProductExpanded = Boolean(expandedProducts[product._id]);
              const productTotals = getProductTotals(product);
              return (
              <div key={product._id} className={styles.productCard}>
                <div className={styles.productHeader}>
                  <div>
                    <h2 className={styles.productName}>{product.name}</h2>
                    <div className={styles.productMeta}>
                      <span className={styles.metaChip}>
                        {t('sizeLabel')} {product.size}
                      </span>
                      <span className={styles.metaChip}>
                        {t('skuLabel')} {product.SKU}
                      </span>
                      <span className={styles.metaChip}>
                        {t('totalAvailable')}: {productTotals.available}
                      </span>
                      <span className={styles.metaChip}>
                        {t('totalHeld')}: {productTotals.held}
                      </span>
                      <span className={styles.valueChip}>
                        {t('availableValue')}: {formatPrice(productTotals.availableValue)}
                      </span>
                      <span className={styles.valueChip}>
                        {t('valueWithHold')}: {formatPrice(productTotals.totalWithHoldValue)}
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    className={styles.expandButton}
                    onClick={() => toggleProduct(product._id)}
                    aria-expanded={isProductExpanded}
                  >
                    {isProductExpanded ? t('hideDetails') : t('showDetails')}
                  </button>
                </div>

                {isProductExpanded && (
                  <>
                    <ProductImage
                      productId={product._id}
                      hasImage={Boolean(product.hasImage)}
                      alt={`Product: ${product.name}`}
                      className={styles.productImage}
                      placeholderClassName={styles.placeholderImage}
                      loadingLabel={t('loadingImage')}
                      noImageLabel={t('noImage')}
                      errorLabel={t('imageUnavailable')}
                    />
                    {(product.sorts || []).map((sort, sortIndex) => {
                      const sortKey = `${product._id}-sort-${sortIndex}`;
                      const isSortExpanded = Boolean(expandedSorts[sortKey]);
                      const colors = Array.isArray(sort.colors) ? sort.colors : [];
                      const sortTotals = getSortTotals(sort);
                      return (
                        <div key={sortKey} className={styles.sortSection}>
                          <div className={styles.sortHeader}>
                            <button
                              type="button"
                              className={styles.sortToggleButton}
                              onClick={() => toggleSort(sortKey)}
                              aria-expanded={isSortExpanded}
                            >
                              <span>{sort.sortName}</span>
                              <span>{isSortExpanded ? t('hideColors') : t('showColors')}</span>
                            </button>
                            <div className={styles.sortMetrics}>
                              {sort.price !== undefined && (
                                <span className={styles.valueChip}>{t('price')}: {formatPrice(sort.price)}</span>
                              )}
                              <span className={styles.colorQuantity}>{t('available')}: {sortTotals.available}</span>
                              <span className={styles.heldBadge}>{t('held')}: {sortTotals.held}</span>
                              <span className={styles.totalBadge}>{t('totalWithHold')}: {sortTotals.totalWithHold}</span>
                              <span className={styles.valueChip}>{t('availableValue')}: {formatPrice(sortTotals.availableValue)}</span>
                              <span className={styles.valueChip}>{t('valueWithHold')}: {formatPrice(sortTotals.totalWithHoldValue)}</span>
                            </div>
                          </div>
                          {isSortExpanded && (
                            <div className={styles.colorList}>
                              {colors.map((color, colorIndex) => (
                                <div key={colorIndex} className={styles.colorItem}>
                                  {(() => {
                                    const quantity = Number(color.quantity) || 0;
                                    const held = Number(color.reservedQuantity) || 0;
                                    const price = Number(sort.price) || 0;
                                    return (
                                      <>
                                        <span className={styles.colorName}>{color.color}</span>
                                        <span className={styles.colorQuantity}>{t('available')}: {quantity}</span>
                                        <span className={styles.heldBadge}>
                                          {t('held')}: {held}
                                        </span>
                                        <span className={styles.totalBadge}>
                                          {t('totalWithHold')}: {quantity + held}
                                        </span>
                                        <span className={styles.valueChip}>
                                          {t('availableValue')}: {formatPrice(quantity * price)}
                                        </span>
                                        <span className={styles.valueChip}>
                                          {t('valueWithHold')}: {formatPrice((quantity + held) * price)}
                                        </span>
                                      </>
                                    );
                                  })()}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </>
                )}
                <div className={styles.buttonGroup}>
                  <button className={styles.editButton} onClick={() => handleEditClick(product)}>
                    {t('edit')}
                  </button>
                  <button className={styles.deleteButton} onClick={() => handleDeleteRequest(product.SKU)}>
                    {t('delete')}
                  </button>
                </div>
              </div>
            )})}
          </div>

          {errorMessage && (
            <div className={styles.modalBackdrop}>
              <div className={styles.modalContent}>
                <h2>{t('warning')}</h2>
                <p>{errorMessage}</p>
                <button onClick={handleCloseModal} className={styles.modalCloseButton}>
                  {t('close')}
                </button>
              </div>
            </div>
          )}

          {confirmDeleteSKU && (
            <div className={styles.modalBackdrop}>
              <div className={styles.modalContent}>
                <h2>{t('confirmDeleteTitle')}</h2>
                <p>
                  {t('confirmDeleteMessage')} {confirmDeleteSKU}?
                </p>
                <div className={styles.modalButtonGroup}>
                  <button onClick={handleDeleteConfirm} className={styles.modalConfirmButton}>
                    {t('yesDelete')}
                  </button>
                  <button onClick={handleDeleteCancel} className={styles.modalCancelButton}>
                    {t('cancel')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {editingProduct && (
            <div className={styles.modalBackdrop}>
              <div className={styles.modalContentLarge}>
                <h2>{t('editProductTitle')}</h2>
                <label className={styles.formLabel}>{t('nameLabel')}</label>
                <input
                  className={styles.formInput}
                  type="text"
                  value={editingProduct.name}
                  onChange={(e) => setEditingProduct({ ...editingProduct, name: e.target.value })}
                  required
                />

                <label className={styles.formLabel}>{t('sizeLabel')}</label>
                <input
                  className={styles.formInput}
                  type="text"
                  value={editingProduct.size}
                  onChange={(e) => setEditingProduct({ ...editingProduct, size: e.target.value })}
                  required
                />

                <label className={styles.formLabel}>{t('skuLabel')}</label>
                <input
                  className={styles.formInput}
                  type="text"
                  value={editingProduct.SKU}
                  onChange={(e) => setEditingProduct({ ...editingProduct, SKU: e.target.value })}
                  required
                />

                <div className={styles.currentImageContainer}>
                  <h3>{editingProduct.hasImage ? t('currentImage') : t('noCurrentImage')}</h3>
                  <ProductImage
                    productId={editingProduct._id}
                    hasImage={Boolean(editingProduct.hasImage)}
                    alt={`Current ${editingProduct.name}`}
                    className={styles.currentImage}
                    placeholderClassName={styles.currentImagePlaceholder}
                    loadingLabel={t('loadingImage')}
                    noImageLabel={t('noImage')}
                    errorLabel={t('imageUnavailable')}
                  />
                </div>

                <label className={styles.formLabel}>{t('updateImageOptional')}</label>
                <input
                  type="file"
                  accept="image/*"
                  className={styles.imageInput}
                  onChange={handleImageChange}
                />

                <h3 className={styles.formHeading}>{t('sortsHeading')}</h3>
                {editingProduct.sorts.map((sort, sortIndex) => (
                  <div key={sortIndex} className={styles.sortContainer}>
                    <input
                      className={styles.sortInput}
                      type="text"
                      value={sort.sortName}
                      onChange={(e) => handleSortChange(sortIndex, 'sortName', e.target.value)}
                      placeholder={t('sortNamePlaceholder')}
                      required
                    />
                    <h4 className={styles.formHeading}>{t('colorsQuantitiesHeading')}</h4>
                    {sort.colors.map((color, colorIndex) => (
                      <div key={colorIndex} className={styles.colorRow}>
                        <input
                          className={styles.colorInput}
                          type="text"
                          value={color.color}
                          onChange={(e) => handleColorChange(sortIndex, colorIndex, 'color', e.target.value)}
                          placeholder={t('colorNamePlaceholder')}
                          required
                        />
                        <input
                          className={styles.quantityInput}
                          type="number"
                          min="0"
                          value={color.quantity}
                          onChange={(e) => handleColorChange(sortIndex, colorIndex, 'quantity', e.target.value)}
                          placeholder={t('quantityPlaceholder')}
                          required
                        />
                        {sort.colors.length > 1 && (
                          <button
                            type="button"
                            className={styles.removeColorButton}
                            onClick={() => handleRemoveColor(sortIndex, colorIndex)}
                          >
                            {t('removeColor')}
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      className={styles.addColorButton}
                      onClick={() => handleAddColor(sortIndex)}
                    >
                      {t('addAnotherColor')}
                    </button>
                    {editingProduct.sorts.length > 1 && (
                      <button
                        type="button"
                        className={styles.removeSortButton}
                        onClick={() => handleRemoveSort(sortIndex)}
                      >
                        {t('removeSort')}
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" className={styles.addSortButton} onClick={handleAddSort}>
                  {t('addAnotherSort')}
                </button>

                <div className={styles.modalButtonGroup}>
                  <button onClick={handleSaveChanges} className={styles.modalConfirmButton}>
                    {t('saveChanges')}
                  </button>
                  <button onClick={handleCancelEdit} className={styles.modalCancelButton}>
                    {t('cancel')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </AppLayout>
  );
};

export default EditProduct;
